import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiUser, FiAward, FiBookOpen, FiGlobe } = FiIcons;

function Attorneys() {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [attorneysRef, attorneysInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const attorneys = [
    {
      name: 'Advocate Given CZ Ndlovu',
      title: 'Senior Partner & Founder',
      email: 'given@gnt.co.za',
      specializations: ['Corporate Law', 'Mergers & Acquisitions', 'Commercial Litigation'],
      languages: ['English', 'Zulu', 'Sotho'],
      education: ['LLB (University of the Witwatersrand)', 'LLM Commercial Law (UCT)'],
      admitted: '1995',
      experience: '30+ years',
      description: 'Given founded the firm with over 30 years of experience in commercial and corporate law. He has handled some of Gauteng\'s most significant corporate restructuring cases and is recognized as a leading authority on South African commercial law.',
      image: 'https://media.phst.co.za/file/gnt-att-given2.jpg'
    },
    {
      name: 'Advocate Mandla Thibela',
      title: 'Partner',
      email: 'mandla@gnt.co.za',
      specializations: ['Employment Law', 'Labor Relations', 'Human Rights'],
      languages: ['English', 'Zulu'],
      education: ['LLB (University of KwaZulu-Natal)', 'LLM Labor Law (Wits)'],
      admitted: '2001',
      experience: '23+ years',
      description: 'Mandla brings exceptional expertise in employment law and has successfully represented both employers and employees in complex labor disputes. He is frequently consulted on employment equity and transformation matters.',
      image: 'https://media.phst.co.za/file/gnt-att-mandla.jpg'
    },
    {
      name: 'Advocate Nomsa DQ Mthembu',
      title: 'Partner',
      email: 'nomsa@gnt.co.za',
      specializations: ['Commercial Litigation', 'Contract Law', 'Dispute Resolution'],
      languages: ['English', 'Afrikaans'],
      education: ['LLB (Stellenbosch University)', 'LLM Litigation (Wits)'],
      admitted: '2003',
      experience: '21+ years',
      description: 'Nomsa is renowned for her litigation skills and has appeared in numerous high-profile commercial disputes. Her strategic approach to conflict resolution has saved clients millions in potential damages.',
      image: 'https://media.phst.co.za/file/gnt-att-nomsa.jpg'
    },
    {
      name: 'Priya CC Sharma',
      title: 'Senior Associate',
      email: 'priya@gnt.co.za',
      specializations: ['Corporate Compliance', 'Regulatory Law', 'International Trade'],
      languages: ['English', 'Hindi'],
      education: ['LLB (University of Cape Town)', 'LLM International Trade (London School of Economics)'],
      admitted: '2008',
      experience: '16+ years',
      description: 'Priya specializes in helping businesses navigate complex regulatory environments and international trade requirements. She advises on compliance strategies for multinational corporations operating in South Africa.',
      image: 'https://media.phst.co.za/file/gnt-att-priya.jpg'
    },
    {
      name: 'Sipho NK Radebe',
      title: 'Senior Associate',
      email: 'sipho@gnt.co.za',
      specializations: ['Employment Law', 'BBBEE Compliance', 'Transformation'],
      languages: ['English', 'Zulu', 'Sotho'],
      education: ['LLB (University of the Witwatersrand)', 'LLM Constitutional Law (UCT)'],
      admitted: '2010',
      experience: '14+ years',
      description: 'Sipho focuses on employment equity and transformation law, helping companies develop and implement effective BBBEE strategies while ensuring compliance with labor legislation.',
      image: 'https://media.phst.co.za/file/gnt-att-sipho.jpg'
    }
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section ref={heroRef} className="relative bg-gradient-to-br from-navy-800 to-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="font-opensans font-light text-4xl lg:text-6xl mb-6 text-white">
              Meet Our <span className="text-gold-600">Legal Team</span>
            </h1>
            <p className="font-opensans text-xl text-gray-200 max-w-3xl mx-auto">
              Our experienced attorneys bring decades of combined expertise in corporate law, employment law, and commercial litigation to serve your legal needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Attorneys Section */}
      <section ref={attorneysRef} className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {attorneys.map((attorney, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={attorneysInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 bg-navy-800 text-gold-600 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiUser} className="w-6 h-6" />
                    </div>
                    <div>
                      <h2 className="font-playfair font-bold text-2xl text-navy-800">
                        {attorney.name}
                      </h2>
                      <p className="font-opensans text-gold-600 font-semibold">
                        {attorney.title}
                      </p>
                    </div>
                  </div>
                  <p className="font-opensans text-gray-700 mb-6 leading-relaxed">
                    {attorney.description}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <h4 className="font-opensans font-semibold text-navy-800 mb-2 flex items-center">
                        <SafeIcon icon={FiAward} className="w-4 h-4 mr-2 text-gold-600" />
                        Specializations
                      </h4>
                      <ul className="space-y-1">
                        {attorney.specializations.map((spec, specIndex) => (
                          <li key={specIndex} className="font-opensans text-gray-600 text-sm">
                            • {spec}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-opensans font-semibold text-navy-800 mb-2 flex items-center">
                        <SafeIcon icon={FiGlobe} className="w-4 h-4 mr-2 text-gold-600" />
                        Languages
                      </h4>
                      <p className="font-opensans text-gray-600 text-sm">
                        {attorney.languages.join(', ')}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <h4 className="font-opensans font-semibold text-navy-800 mb-2 flex items-center">
                        <SafeIcon icon={FiBookOpen} className="w-4 h-4 mr-2 text-gold-600" />
                        Education
                      </h4>
                      <ul className="space-y-1">
                        {attorney.education.map((edu, eduIndex) => (
                          <li key={eduIndex} className="font-opensans text-gray-600 text-sm">
                            • {edu}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-opensans font-semibold text-navy-800 mb-2">
                        Experience
                      </h4>
                      <p className="font-opensans text-gray-600 text-sm mb-2">
                        {attorney.experience}
                      </p>
                      <p className="font-opensans text-gray-600 text-sm">
                        Admitted: {attorney.admitted}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 text-gold-600">
                    <SafeIcon icon={FiMail} className="w-4 h-4" />
                    <a
                      href={`mailto:${attorney.email}`}
                      className="font-opensans hover:text-gold-700 transition-colors"
                    >
                      {attorney.email}
                    </a>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div className="relative">
                    <img
                      src={attorney.image}
                      alt={attorney.name}
                      className="rounded-lg shadow-xl w-full h-96 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-800/20 to-transparent rounded-lg"></div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-playfair font-bold text-4xl text-navy-800 mb-4">
              Our Team by the Numbers
            </h2>
            <p className="font-opensans text-xl text-gray-600">
              Experience and expertise you can trust
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: '120+', label: 'Combined Years of Experience' },
              { number: '500+', label: 'Cases Successfully Handled' },
              { number: '5', label: 'Expert Attorneys' },
              { number: '3', label: 'Languages Spoken' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="font-playfair font-bold text-4xl text-navy-800 mb-2">
                  {stat.number}
                </div>
                <div className="font-opensans text-gray-600">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-playfair font-bold text-4xl mb-4">
            Ready to Work with Our Team?
          </h2>
          <p className="font-opensans text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Contact us today to schedule a consultation with one of our experienced attorneys. We're here to help you navigate your legal challenges with confidence.
          </p>
          <a
            href="/contact"
            className="bg-gold-500 text-navy-800 px-8 py-4 rounded-lg font-opensans font-semibold hover:bg-gold-600 transition-colors duration-200 inline-block"
          >
            Schedule Consultation
          </a>
        </div>
      </section>
    </div>
  );
}

export default Attorneys;